import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Camera, Upload, Trash2, Edit2, X, GripVertical, Image as ImageIcon, Star } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CATEGORIES = [
  { id: "gala", label: "Gala & Awards" },
  { id: "community", label: "Community Outreach" },
  { id: "ride", label: "Awareness Rides" },
  { id: "stadium", label: "Stadium Events" },
  { id: "law_enforcement", label: "First Responders" },
  { id: "media", label: "Media" }
];

export default function AdminGallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "community",
    is_featured: false,
    file: null
  });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`, { withCredentials: true });
      setImages(response.data);
    } catch (error) {
      console.error("Error fetching gallery:", error);
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!formData.file) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append("file", formData.file);
    uploadData.append("title", formData.title || formData.file.name);
    uploadData.append("description", formData.description);
    uploadData.append("category", formData.category);

    try {
      await axios.post(`${API}/gallery/upload`, uploadData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Image uploaded successfully!");
      setUploadDialogOpen(false);
      setFormData({ title: "", description: "", category: "community", is_featured: false, file: null });
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchImages();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/gallery/${selectedImage.image_id}`, {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        is_featured: formData.is_featured
      }, { withCredentials: true });
      toast.success("Image updated successfully!");
      setEditDialogOpen(false);
      fetchImages();
    } catch (error) {
      console.error("Edit error:", error);
      toast.error("Failed to update image");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/gallery/${selectedImage.image_id}`, { withCredentials: true });
      toast.success("Image deleted successfully!");
      setDeleteDialogOpen(false);
      setSelectedImage(null);
      fetchImages();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete image");
    }
  };

  const openEditDialog = (image) => {
    setSelectedImage(image);
    setFormData({
      title: image.title,
      description: image.description || "",
      category: image.category,
      is_featured: image.is_featured || false,
      file: null
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (image) => {
    setSelectedImage(image);
    setDeleteDialogOpen(true);
  };

  const filteredImages = filterCategory === "all" 
    ? images 
    : images.filter(img => img.category === filterCategory);

  const getCategoryLabel = (categoryId) => {
    const cat = CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.label : categoryId;
  };

  return (
    <AdminLayout currentPage="Gallery">
      <div className="p-8" data-testid="admin-gallery">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-heading-2 text-deep-blue">Photo Gallery</h1>
            <p className="text-slate-600">Manage photos displayed on the website</p>
          </div>
          <Button className="btn-primary" onClick={() => setUploadDialogOpen(true)} data-testid="upload-btn">
            <Upload className="w-4 h-4 mr-2" />
            Upload Photo
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-awareness-blue/10 rounded-full flex items-center justify-center">
                <Camera className="w-5 h-5 text-awareness-blue" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Photos</p>
                <p className="text-2xl font-bold text-deep-blue">{images.length}</p>
              </div>
            </div>
          </div>
          {CATEGORIES.slice(0, 3).map(cat => (
            <div key={cat.id} className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{cat.label}</p>
                  <p className="text-2xl font-bold text-deep-blue">
                    {images.filter(img => img.category === cat.id).length}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="mb-6 flex gap-4 items-center">
          <Label>Filter by category:</Label>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="aspect-square bg-slate-200 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : filteredImages.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredImages.map(image => (
              <div
                key={image.image_id}
                className="group relative aspect-square bg-slate-100 rounded-lg overflow-hidden shadow-sm"
                data-testid={`gallery-item-${image.image_id}`}
              >
                <img
                  src={image.url || `${API}/gallery/image/${image.image_id}`}
                  alt={image.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                
                {/* Featured badge */}
                {image.is_featured && (
                  <div className="absolute top-2 left-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 shadow-md">
                    <Star className="w-3 h-3 fill-current" />
                    Featured
                  </div>
                )}
                
                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => openEditDialog(image)}
                      className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center"
                      data-testid={`edit-${image.image_id}`}
                    >
                      <Edit2 className="w-4 h-4 text-white" />
                    </button>
                    <button
                      onClick={() => openDeleteDialog(image)}
                      className="w-8 h-8 bg-red-500/80 hover:bg-red-500 rounded-full flex items-center justify-center"
                      data-testid={`delete-${image.image_id}`}
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  </div>
                  
                  <div>
                    <p className="text-white font-medium text-sm truncate">{image.title}</p>
                    <span className="text-xs text-white/70">{getCategoryLabel(image.category)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg p-12 text-center">
            <Camera className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No photos yet</h3>
            <p className="text-slate-500 mb-4">Start building your gallery by uploading photos</p>
            <Button className="btn-primary" onClick={() => setUploadDialogOpen(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Upload First Photo
            </Button>
          </div>
        )}

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Photo</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleUpload} className="space-y-6">
              <div>
                <Label htmlFor="file">Select Image *</Label>
                <Input
                  ref={fileInputRef}
                  id="file"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormData({...formData, file: e.target.files[0]})}
                  required
                  className="mt-1"
                  data-testid="file-input"
                />
                <p className="text-xs text-slate-500 mt-1">Supported: JPG, PNG, GIF, WebP</p>
              </div>

              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Photo title"
                  data-testid="title-input"
                />
              </div>

              <div>
                <Label htmlFor="category">Category *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({...formData, category: v})}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of the photo"
                  rows={3}
                  data-testid="description-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setUploadDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={uploading} data-testid="submit-upload">
                  {uploading ? "Uploading..." : "Upload"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Photo</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleEdit} className="space-y-6">
              {selectedImage && (
                <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden">
                  <img
                    src={selectedImage.url || `${API}/gallery/image/${selectedImage.image_id}`}
                    alt={selectedImage.title}
                    className="w-full h-full object-contain"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="edit-title">Title</Label>
                <Input
                  id="edit-title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Photo title"
                />
              </div>

              <div>
                <Label htmlFor="edit-category">Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({...formData, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of the photo"
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <Checkbox
                  id="edit-featured"
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => setFormData({...formData, is_featured: checked})}
                />
                <div>
                  <Label htmlFor="edit-featured" className="flex items-center gap-2 cursor-pointer">
                    <Star className="w-4 h-4 text-yellow-500" />
                    Featured on Homepage
                  </Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    This photo will appear in the homepage gallery preview section
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Photo</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedImage?.title}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
